/**
 * FavTabPicker — bottom-sheet that lets the user pick which Tools page sits
 * in the 5th mobile tab slot.
 *
 *   • Opens from a long-press on the 5th FloatingTabBar tab.
 *   • Search field at the top + categorized grid below.
 *   • Tap a tool to select; closes with a soft haptic.
 *   • Glass surface, gradient ring, drag-handle, scrim with brand tints.
 *   • Selected item glows with its accent color.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlass, X, Check, Lightning } from '@phosphor-icons/react';

const haptic = () => { try { navigator.vibrate?.(8); } catch { /* noop */ } };

const FavTabPicker = ({ open, onClose, tools, currentPath, onSelect, slotIndex = null }) => {
  const [query, setQuery] = useState('');

  /* Reset query on open. */
  useEffect(() => { if (open) setQuery(''); }, [open]);

  const slotLabel = slotIndex == null ? null : (
    ['1st', '2nd', '3rd', '4th', '5th'][slotIndex] || `${slotIndex + 1}th`
  );

  /* Body scroll lock + ESC to close. */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  /* Group tools by category, filtered by query. */
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = (tools || []).filter(t => {
      if (!q) return true;
      return (
        (t.label || '').toLowerCase().includes(q) ||
        (t.desc || '').toLowerCase().includes(q) ||
        (t.category || '').toLowerCase().includes(q)
      );
    });
    const byCat = new Map();
    filtered.forEach(t => {
      const k = t.category || 'Other';
      if (!byCat.has(k)) byCat.set(k, []);
      byCat.get(k).push(t);
    });
    return Array.from(byCat.entries()).map(([category, items]) => ({ category, items }));
  }, [tools, query]);

  const pick = (t) => { haptic(); onSelect?.(t.path); onClose(); };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Scrim */}
          <motion.div
            key="favpick-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.20 }}
            className="favpick-scrim"
            onClick={onClose}
            data-testid="favpick-scrim"
            aria-hidden />

          {/* Sheet */}
          <motion.div
            key="favpick-sheet"
            initial={{ y: '105%' }}
            animate={{ y: 0 }}
            exit={{ y: '105%' }}
            transition={{ type: 'spring', stiffness: 360, damping: 38 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.18 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 320) onClose(); }}
            className="favpick-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Choose 5th tab"
            data-testid="favpick-sheet">

            {/* Ambient layer */}
            <div aria-hidden className="favpick-ambient">
              <span className="favpick-blob favpick-blob-1" />
              <span className="favpick-blob favpick-blob-2" />
            </div>

            {/* Drag handle */}
            <div className="favpick-handle" aria-hidden />

            {/* Header */}
            <div className="favpick-header">
              <div className="favpick-title">
                <span className="favpick-title-pip" />
                <h2>{slotLabel ? `Edit ${slotLabel} tab` : 'Pick your shortcut'}</h2>
              </div>
              <p className="favpick-subtitle">
                {slotLabel
                  ? `Choose any page to live in your ${slotLabel} bottom-tab slot.`
                  : 'Choose the Tools page that lives in your 5th tab.'}
              </p>
              <button type="button" onClick={onClose}
                className="favpick-close" aria-label="Close"
                data-testid="favpick-close">
                <X size={14} weight="bold" />
              </button>
            </div>

            {/* Search */}
            <div className="favpick-search">
              <MagnifyingGlass size={13} weight="bold" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tools…"
                autoFocus={false}
                data-testid="favpick-search" />
            </div>

            {/* Grid */}
            <div className="favpick-body cs-hide-scrollbar">
              {groups.length === 0 ? (
                <div className="favpick-empty">No tools match "{query}"</div>
              ) : (
                groups.map(g => (
                  <div key={g.category} className="favpick-group">
                    <div className="favpick-group-label">{g.category}</div>
                    <div className="favpick-grid">
                      {g.items.map(t => {
                        const accent = t.color || '#A3A3A3';
                        const Icon = t.icon;
                        const selected = t.path === currentPath;
                        return (
                          <button
                            key={t.path}
                            type="button"
                            onClick={() => pick(t)}
                            className={`favpick-card ${selected ? 'is-selected' : ''}`}
                            style={{ '--card-accent': accent }}
                            data-testid={`favpick-card-${t.path}`}>
                            <span className="favpick-card-glow" aria-hidden />
                            <span className="favpick-card-icon">
                              {Icon ? <Icon size={20} weight={selected ? 'fill' : 'regular'} /> : <Lightning size={20} />}
                            </span>
                            <span className="favpick-card-label">{t.label}</span>
                            {selected && (
                              <span className="favpick-card-check" aria-hidden>
                                <Check size={11} weight="bold" />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="favpick-footer">
              <span>Tip: long-press any tab to customize it.</span>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default FavTabPicker;
